import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Heart } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DISCLOSURE_VERSION = '2026-10-21';

const GetInvolvedSection = ({ onDonate, onVolunteer, onNewsletter }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    zipCode: '',
    phone: ''
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If opting into SMS, require a valid mobile phone number (at least 10 digits)
    if (smsConsent) {
      const digits = (formData.phone || '').replace(/[^0-9]/g, '');
      if (digits.length < 10) {
        toast({
          title: "Mobile number required",
          description: "To receive text messages, please enter a valid mobile phone number.",
          variant: "destructive"
        });
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/volunteer`, {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone,
        zip: formData.zipCode,
        sms_consent: smsConsent,
        disclosure_version: DISCLOSURE_VERSION
      });

      toast({
        title: "Thank you for volunteering!",
        description: response.data.message || "We'll be in touch soon with ways to help."
      });

      setFormData({ firstName: '', lastName: '', email: '', zipCode: '', phone: '' });
      setSmsConsent(false);
    } catch (error) {
      console.error('Volunteer submission error:', error);
      toast({
        title: "Submission failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="get-involved" className="py-20 lg:py-32 bg-white">
      <div className="container mx-auto px-6 lg:px-20">
        <div className="max-w-5xl mx-auto">
          {/* Clear Header with Better Spacing */}
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-8 leading-[1.1] uppercase tracking-tight">
              Get Involved
            </h2>
            <p className="text-xl lg:text-2xl xl:text-3xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
              Join our grassroots movement to deliver real results for Rochester
            </p>
          </div>
          
          {/* Two Column Layout with Better Spacing */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
            {/* Left: Volunteer Form - Clean & Uncluttered */}
            <div className="bg-slate-50 p-8 lg:p-10 rounded-2xl shadow-lg">
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-2 uppercase tracking-tight">
                Volunteer
              </h3>
              <p className="text-base text-slate-600 mb-8">
                Help us knock on doors, make calls, and spread the word
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    disabled={isSubmitting}
                    className="h-12 lg:h-14 bg-white text-slate-800 placeholder:text-slate-500 border-slate-300 rounded-lg text-base"
                  />
                  <Input
                    type="text"
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    disabled={isSubmitting}
                    className="h-12 lg:h-14 bg-white text-slate-800 placeholder:text-slate-500 border-slate-300 rounded-lg text-base"
                  />
                </div>
                
                <Input
                  type="email"
                  placeholder="Email address*"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={isSubmitting}
                  className="h-12 lg:h-14 bg-white text-slate-800 placeholder:text-slate-500 border-slate-300 rounded-lg text-base"
                />
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    type="tel"
                    placeholder={smsConsent ? "Mobile phone*" : "Phone"}
                    aria-label="Mobile phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required={smsConsent}
                    disabled={isSubmitting}
                    data-testid="volunteer-phone-input"
                    className="h-12 lg:h-14 bg-white text-slate-800 placeholder:text-slate-500 border-slate-300 rounded-lg text-base"
                  />
                  <Input
                    type="text"
                    placeholder="Zip code*"
                    pattern="[0-9]{5}"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    required
                    disabled={isSubmitting}
                    className="h-12 lg:h-14 bg-white text-slate-800 placeholder:text-slate-500 border-slate-300 rounded-lg text-base"
                  />
                </div>
                
                {/* SMS/MMS Opt-In Disclosure */}
                <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-3" data-testid="sms-optin-disclosure">
                  <p className="text-xs text-slate-600 leading-relaxed">
                    By entering your phone number and selecting to opt in, you consent to join a recurring SMS/MMS
                    text messaging program that will provide alerts, donation requests, updates, and other
                    important information. By participating, you agree to the{' '}
                    <Link to="/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="text-campaign-blue font-semibold underline" data-testid="form-terms-link">
                      Terms &amp; Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-campaign-blue font-semibold underline" data-testid="form-privacy-link">
                      Privacy Policy
                    </Link>{' '}
                    for autodialed messages from Friends of Clianda Florence to the number you provide. Message
                    frequency varies. Msg &amp; data rates may apply. Reply HELP for help or STOP to opt out at any
                    time. Email us at{' '}
                    <a href="mailto:cliandaflorence@friendsofcliandaflorence.com" className="text-campaign-blue font-semibold underline">
                      cliandaflorence@friendsofcliandaflorence.com
                    </a>{' '}
                    for assistance. SMS opt-in information and consent are not sold, rented, or shared with third
                    parties for their own marketing purposes.
                  </p>

                  {/* Optional consent checkbox */}
                  <div className="flex items-start gap-3 pt-1">
                    <input
                      type="checkbox"
                      id="sms-consent"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      disabled={isSubmitting}
                      className="mt-1 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-slate-400 text-campaign-blue focus:ring-2 focus:ring-campaign-blue"
                      data-testid="sms-consent-checkbox"
                    />
                    <label htmlFor="sms-consent" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                      I agree to receive recurring SMS/MMS text messages from Friends of Clianda Florence.
                    </label>
                  </div>

                  <p className="text-xs text-slate-500 leading-relaxed">
                    By entering your mobile phone number, checking this optional box, and submitting this form, you
                    consent to join a recurring SMS/MMS text messaging program from Friends of Clianda Florence.
                    Messages may be sent using an autodialer to the number you provide.
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    <span className="font-semibold">Privacy Policy instructions:</span> To learn how we collect,
                    use, and protect your information, click the Privacy Policy link in this form. The Privacy
                    Policy is also available under the Legal section in the website footer.
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    <span className="font-semibold">How to opt out:</span> Reply STOP at any time to opt out and
                    stop receiving messages.
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    <span className="font-semibold">How to receive help:</span> Reply HELP for help or email{' '}
                    <a href="mailto:cliandaflorence@friendsofcliandaflorence.com" className="text-campaign-blue font-semibold underline">
                      cliandaflorence@friendsofcliandaflorence.com
                    </a>{' '}
                    for assistance.
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    <span className="font-semibold">Message and data rates:</span> Msg &amp; data rates may apply.
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    <span className="font-semibold">Message frequency:</span> Message frequency varies.
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    <span className="font-semibold">Types of messages:</span> Messages may include campaign alerts,
                    donation and fundraising requests, campaign updates, voter outreach, candidate information,
                    volunteer opportunities, event invitations and reminders, polling questions, surveys,
                    get-out-the-vote reminders, election information, public service announcements, and other
                    important campaign information.
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    By participating, you agree to the{' '}
                    <Link to="/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="text-campaign-blue font-semibold underline">
                      Terms &amp; Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-campaign-blue font-semibold underline">
                      Privacy Policy
                    </Link>
                    . Consent is not a condition of making a donation, volunteering, supporting the campaign,
                    purchasing anything, or using this website.
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    SMS opt-in information and consent are not sold, rented, or shared with third parties for their
                    own marketing purposes.
                  </p>
                  <p className="text-xs font-bold text-slate-700">
                    Paid for by Friends of Clianda Florence.
                  </p>
                </div>

                {/* Strong CTA */}
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 lg:h-14 bg-campaign-blue hover:bg-blue-800 text-white font-extrabold uppercase text-base lg:text-lg rounded-lg transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="volunteer-submit-button"
                >
                  {isSubmitting ? 'Submitting...' : 'Sign Up to Volunteer'}
                </Button>
              </form>
              
              <p className="text-xs text-slate-500 mt-4 text-center">
                Submissions sent to cliandaflorence@friendsofcliandaflorence.com
              </p>
            </div>
            
            {/* Right: Donate - Clear & Professional */}
            <div className="bg-campaign-red text-white p-8 lg:p-10 rounded-2xl shadow-lg">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 lg:w-14 lg:h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <Heart className="h-6 w-6 lg:h-7 lg:w-7 text-white" />
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold uppercase tracking-tight">
                  Donate
                </h3>
              </div>
              
              <p className="text-base text-white/90 mb-8">
                Power our grassroots, people-funded campaign
              </p>
              
              {/* Matching Funds Info - Clear & Scannable */}
              <div className="bg-white/15 backdrop-blur-sm border border-white/30 rounded-xl p-6 mb-8">
                <p className="text-sm font-bold mb-3 leading-relaxed">
                  New York State matches donations $5-$250, multiplying your impact
                </p>
                <p className="text-xs text-white/90 leading-relaxed">
                  Every dollar counts. With matching funds, even small gifts go much further.
                </p>
              </div>
              
              {/* Donation Amounts - Good Spacing */}
              <div className="space-y-3">
                <Button 
                  onClick={() => onDonate(25)}
                  className="w-full h-12 lg:h-14 bg-white text-campaign-red hover:bg-slate-100 font-bold text-base lg:text-lg rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Donate $25
                </Button>
                <Button 
                  onClick={() => onDonate(50)}
                  className="w-full h-12 lg:h-14 bg-white text-campaign-red hover:bg-slate-100 font-bold text-base lg:text-lg rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Donate $50
                </Button>
                <Button 
                  onClick={() => onDonate(100)}
                  className="w-full h-12 lg:h-14 bg-white text-campaign-red hover:bg-slate-100 font-bold text-base lg:text-lg rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Donate $100
                </Button>
                <Button 
                  onClick={() => onDonate(250)}
                  className="w-full h-12 lg:h-14 bg-white text-campaign-red hover:bg-slate-100 font-bold text-base lg:text-lg rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Donate $250
                </Button>
                <Button 
                  onClick={() => onDonate('custom')}
                  className="w-full h-12 lg:h-14 bg-white/10 border-2 border-white text-white hover:bg-white/20 font-bold text-base lg:text-lg rounded-lg transition-all duration-300"
                >
                  Custom Amount
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { GetInvolvedSection };
